// THE RECORDS BLOCK — one renderer for a RECORDS BLOCK v1 wherever it lands:
// a generated board's card (AssistantBoardView, kind 'records') and an inline
// thread block (AssistantThreadBlock, `{ type: 'records', block }`).
// AI_ASSISTANT_SPEC v1 §2–§3; DESIGN-WL-ASSISTANT-MAIN-VIEW-1.
//
// The block is declarative; its rows come from `ai.boards.records` under the
// member's grants (functions/assistantRecords.js). Four views — a STAT (one
// number, the period and the count under it), a LIST (title + two facts per
// row), a TABLE (up to five columns), a CHART (one bar per group) — and the
// honest states: a skeleton while loading, one sentence when nothing matches,
// when the service is not there yet, when the read is refused, and when it
// fails (with Try again). A row opens its record through openInSlot. Tokens
// only; the component reads its block from its state (`records` on a board
// row, `block` on a thread card) and holds no logic of its own.
export const AssistantRecordsBlock = {
  // The view it renders as: the server's own when the answer names one (a
  // list grouped by a field comes back as a chart), else the block's.
  dataRecordsView: (el, s) => {
    const b = el.call('recordsBlockOf', s)
    if (!b) return null
    return el.call('assistantRecordsViewOf', b, el.call('assistantRecordsEntry', s && s.root, b))
  },
  dataRecordsStatus: (el, s) => {
    const e = el.call('assistantRecordsEntry', s && s.root, el.call('recordsBlockOf', s))
    return (e && e.status) || 'idle'
  },
  ariaBusy: (el, s) => {
    const e = el.call('assistantRecordsEntry', s && s.root, el.call('recordsBlockOf', s))
    return !e || e.status === 'loading' ? 'true' : null
  },
  flow: 'y',
  gap: 'Z',
  width: '100%',
  minWidth: '0',
  // Read once the node exists; again when the block or the workspace changes
  // (the cache is per workspace + block, so a repeat costs nothing).
  onRender: (el, s) => el.call('assistantRecordsEnsure', el, s),
  onUpdate: (el, s) => el.call('assistantRecordsEnsure', el, s),

  // Loading: the stat's hero bar and three row bars, in the view's place.
  RecordsSkeleton: {
    hide: (el, s) => {
      const e = el.call('assistantRecordsEntry', s && s.root, el.call('recordsBlockOf', s))
      return !!e && e.status !== 'loading'
    },
    flow: 'y',
    gap: 'Y',
    Skeleton_1: { width: '40%', height: 'B' },
    Skeleton_2: { width: '100%', height: 'A' },
    Skeleton_3: { width: '80%', height: 'A' }
  },

  // THE VIEW. Only the one the answer draws EXISTS (`if:`, never a hidden
  // twin): a card carries its stat OR its list OR its table OR its chart —
  // a board of twelve cards does not build forty-eight views to show twelve.
  // The holder itself stays mounted, so the view keeps its place above the
  // lines under it whenever the answer lands.
  RecordsView: {
    hide: (el, s) => !el.call('assistantRecordsShows', s),
    minWidth: '0',
    childrenAs: 'state',
    children: (el, s) => {
      if (!el.call('assistantRecordsShows', s)) return []
      // Derive once in this reactive effect. All nested data and language
      // reads stay tracked; no identity cache can retain a stale answer.
      const model = el.call('assistantRecordsViewFor', s)
      // A bar or a chip opens its records only where a drill lands (a ready
      // board, the person's own done Fly page — functions/assistantRecords.js
      // assistantRecordsDrillFrom); anywhere else it only reads.
      const drill = model.bars || model.chips ? el.call('assistantRecordsDrillFrom', s) : null
      if (drill && model.bars) model.bars = model.bars.map((bar) => ({ ...bar, ...drill }))
      if (drill && model.chips) model.chips = model.chips.map((chip) => ({ ...chip, ...drill }))
      return [
        {
          key: 'records-content',
          records: el.call('recordsBlockOf', s),
          hue: s && s.hue,
          recordsViewModel: model,
          recordsGeometry: model.line
            ? el.call('homeSparkGeom', model.line.points, 240, 64, 4)
            : null
        }
      ]
    },
    // One stable child owns the current render model. Its children share
    // this state without adding a public component to the registry.
    childProps: {
      tag: 'div',
      flow: 'y',
      gap: 'Z',
      minWidth: '0',

      // STAT — the home widget's own parts, so an answer's number reads like
      // its home twin (render step 2): the HERO numeral with its unit word (the
      // money code leads: "GEL" · "64.6K"), a split per currency when the set
      // mixes currencies, the TREND chip against the span before
      // (aggregate.previous; absent without one — never a fake 0%), the SPARK of
      // the period (series, two points or more), then the period and the count.
      // The accent (the chip's dot, the spark) is the board card's app hue, else
      // the text ink. BOTH halves of that rule now live in the card's own
      // `--home-widget-accent` (functions/homeWidgetFrame.js): a hue that reads
      // on the card surface resolves to the hue, and a hue-less card — or a hue
      // with no readable accent in its family — resolves to the title ink. So
      // this slot reads ONE variable instead of re-deciding, and the var's CSS
      // fallback carries the one host that publishes no card vars at all: the
      // thread (AssistantThreadBlock), where the block renders outside a widget
      // card. It must never read `--home-widget-hue` again — that is the PLATE,
      // a surface, and on a dark plate family it drew the bars all but invisible
      // (dev 2026-10-04: WIDGET-TOOLS-RECORDS-SHAPE-1 for the hue-less case,
      // HOME-WIDGET-ACCENT-DARK-PLATE-INVISIBLE-1 for the hued one).
      RecordsStat: {
        if: (el, s) => s.recordsViewModel && s.recordsViewModel.view === 'stat',
        flow: 'y',
        gap: 'Y',
        minWidth: '0',
        color: 'var(--home-widget-accent, var(--color-title))',
        AssistantRecordsHero: { Value: { dataRecordsValue: true } },
        StatCurrencies: {
          flow: 'x',
          flexWrap: 'wrap',
          gap: 'Z',
          childrenAs: 'state',
          children: (el, s) => s.recordsViewModel.currencies || [],
          childExtends: 'AppMicroLabel',
          childProps: { text: (el, s) => (s && s.text) || '' }
        },
        HomeWidgetDelta: {
          if: (el, s) => !!(s.recordsViewModel && s.recordsViewModel.delta),
          Dot: { background: (el) => (el.parent.bad ? 'dangerInk' : 'currentColor') },
          Text: { text: (el, s) => s.recordsViewModel.delta || '' }
        },
        HomeWidgetSpark: {
          if: (el, s) => !!(s.recordsViewModel && s.recordsViewModel.spark),
          // shown at every card size — the home hides it only in an S cell
          display: 'block',
          points: (el, s) => s.recordsViewModel.spark
        },
        StatSub: {
          tag: 'span',
          fontSize: 'Z',
          color: 'caption',
          text: (el, s) => el.call('assistantRecordsSub', s)
        }
      },

      // LIST: one row per record — its title, then up to two facts.
      RecordsList: {
        if: (el, s) => s.recordsViewModel && s.recordsViewModel.view === 'list',
        tag: 'ul',
        margin: '0',
        padding: '0',
        listStyle: 'none',
        flow: 'y',
        gap: 'Y',
        childrenAs: 'state',
        children: (el, s) => s.recordsViewModel.rows || [],
        childExtends: 'AssistantRecordsRow'
      },

      // TABLE: up to five columns; the row opens its record.
      RecordsTable: {
        if: (el, s) => s.recordsViewModel && s.recordsViewModel.view === 'table',
        overflowX: 'auto',
        minWidth: '0',
        RecordsTableEl: {
          tag: 'table',
          width: '100%',
          minWidth: (el, s) => (s.recordsViewModel?.columns?.length > 2 ? 'H' : '100%'),
          borderCollapse: 'collapse',
          fontSize: 'Z',
          RecordsHead: {
            tag: 'thead',
            RecordsHeadRow: {
              tag: 'tr',
              childrenAs: 'state',
              children: (el, s) => s.recordsViewModel.columns || [],
              childExtends: 'AssistantRecordsHeadCell'
            }
          },
          RecordsBody: {
            tag: 'tbody',
            childrenAs: 'state',
            children: (el, s) => s.recordsViewModel.rows || [],
            childExtends: 'AssistantRecordsTableRow'
          }
        }
      },

      // How many of all that match the list or table holds ("20 of 46 shown").
      RecordsMore: {
        tag: 'p',
        dataRecordsMore: true,
        if: (el, s) => !!(s.recordsViewModel && s.recordsViewModel.more),
        margin: '0',
        fontSize: 'Z',
        color: 'caption',
        text: (el, s) => (s.recordsViewModel && s.recordsViewModel.more) || ''
      },

      // CHART — drawn by the data's shape (render step 2): groups over TIME are
      // a line with its newest value at the accent dot (AssistantRecordsLine);
      // categories are bars, largest first, each with its value, at most eight
      // (the rest folds into "Others" when the numbers add up). The accent is
      // the board card's app hue, else the text ink (as RecordsStat).
      RecordsChart: {
        if: (el, s) => s.recordsViewModel && s.recordsViewModel.view === 'chart',
        flow: 'y',
        gap: 'Y',
        minWidth: '0',
        color: 'var(--home-widget-accent, var(--color-title))',
        RecordsBars: {
          if: (el, s) => s.recordsViewModel && s.recordsViewModel.chart === 'bars',
          dataRecordsBars: true,
          flow: 'y',
          gap: 'Y',
          minWidth: '0',
          childrenAs: 'state',
          // Where a drill lands (a board card, a done Fly section) a bar opens
          // its records (D2 drill-down): the card's key rides the bar as
          // `from`, a Fly section's page as `page`. A thread card has nowhere
          // to take the drill card, so its bars stay plain.
          children: (el, s) => s.recordsViewModel.bars || [],
          childExtends: 'AssistantRecordsBar'
        },
        // D5 THE DONUT — parts of a whole: the ring (a track + one slice per
        // group, the card hue at one opacity step per slice), the total in the
        // hole, and beside it the legend: the bar rows with a dot for the
        // track and the share after the value (drillable like a bar).
        RecordsDonut: {
          if: (el, s) => s.recordsViewModel && s.recordsViewModel.chart === 'donut',
          dataRecordsDonut: true,
          flow: 'x',
          flexWrap: 'wrap',
          align: 'center',
          gap: 'A',
          minWidth: '0',
          DonutPlot: {
            position: 'relative',
            boxSize: 'E',
            flexShrink: '0',
            Ring: {
              // frank-allow FA601 — data plot (non-icon viewBox), not an icon
              tag: 'svg',
              viewBox: '0 0 42 42',
              ariaHidden: 'true',
              width: '100%',
              height: '100%',
              display: 'block',
              DonutTrack: {
                tag: 'circle',
                attr: {
                  cx: '21',
                  cy: '21',
                  r: '15.9155',
                  fill: 'none',
                  stroke: 'currentColor',
                  'stroke-opacity': '0.08',
                  'stroke-width': '6'
                }
              },
              DonutSlices: {
                tag: 'g',
                childrenAs: 'state',
                children: (el, s) => {
                  const d = s.recordsViewModel.donut
                  return (d && d.slices) || []
                },
                childExtends: 'AssistantRecordsSlice'
              }
            },
            DonutTotal: {
              dataDonutTotal: true,
              position: 'absolute',
              inset: '0',
              flow: 'x',
              align: 'center center',
              fontSize: 'A',
              fontWeight: '500',
              color: 'title',
              fontVariantNumeric: 'tabular-nums',
              text: (el, s) => {
                const d = s.recordsViewModel.donut
                return (d && d.total) || ''
              }
            }
          },
          DonutLegend: {
            dataRecordsBars: true,
            flex: '1',
            flow: 'y',
            gap: 'X',
            minWidth: 'D',
            maxWidth: '100%',
            childrenAs: 'state',
            children: (el, s) => s.recordsViewModel.bars || [],
            childExtends: 'AssistantRecordsBar'
          }
        },
        AssistantRecordsLine: {
          if: (el, s) => s.recordsViewModel && s.recordsViewModel.chart === 'line'
        },
        // CHIPS — the display hint 'chips' (board binding A′) on categories:
        // one wrapped row of pills, each its group's label and its count, in
        // the bars' order and fold. Where a drill lands a chip opens its
        // records like a bar (D2 drill-down).
        RecordsChips: {
          if: (el, s) => s.recordsViewModel && s.recordsViewModel.chart === 'chips',
          dataRecordsChips: true,
          flow: 'x',
          flexWrap: 'wrap',
          gap: 'X',
          minWidth: '0',
          childrenAs: 'state',
          children: (el, s) => s.recordsViewModel.chips || [],
          childExtends: 'AssistantRecordsChip'
        }
      }
    }
  },

  // A DRILL card (D2) names the filter it came from, in the WHY line's
  // voice: "Stage: Proposal · from Deals by stage".
  RecordsDrill: {
    tag: 'p',
    dataRecordsDrill: true,
    if: (el, s) => !!el.call('assistantRecordsDrillWords', s),
    margin: '0',
    fontSize: 'Z',
    color: 'caption',
    overflowWrap: 'anywhere',
    text: (el, s) => el.call('assistantRecordsDrillWords', s)
  },

  // How the answer was read, when the reader must know it — one quiet line
  // under the rows: a value the server read as the real one ("Read “tower a”
  // as “Tower A”"), a money sort that stays inside each currency.
  RecordsWarning: {
    tag: 'p',
    dataRecordsWarning: true,
    hide: (el, s) => !el.call('assistantRecordsWarning', s),
    margin: '0',
    fontSize: 'Z',
    color: 'caption',
    overflowWrap: 'anywhere',
    text: (el, s) => el.call('assistantRecordsWarning', s)
  },

  // Every other state is ONE sentence (never a bare zero); a failure offers
  // Try again. A status region exists before it speaks.
  RecordsNote: {
    role: 'status',
    flow: 'x',
    flexWrap: 'wrap',
    align: 'center',
    gap: 'Z',
    minWidth: '0',
    NoteText: {
      tag: 'span',
      fontSize: 'Z',
      color: 'caption',
      overflowWrap: 'anywhere',
      text: (el, s) => el.call('assistantRecordsNote', s)
    },
    AppCapsuleBtnToolbar: {
      compact: true,
      dataRecordsRetry: true,
      hide: (el, s) => {
        const e = el.call('assistantRecordsEntry', s && s.root, el.call('recordsBlockOf', s))
        return !e || e.status !== 'failed'
      },
      text: '{{ workspace.assistantBoard.tryAgain | polyglot }}',
      onClick: (e, el, s) => el.call('assistantRecordsEnsure', el, s, true)
    }
  }
}

// THE HERO of a stat or a line: the home widget's numeral with its unit word
// (the money code leads: "GEL" · "64.6K"). A stat's `hero` and a line's
// newest value (`line`) carry the same { number, unit } pair.
export const AssistantRecordsHero = {
  extends: 'HomeWidgetHero',
  unitFirst: true,
  Value: {
    text: (el, s) => {
      const v = (s && s.recordsViewModel) || el.call('assistantRecordsViewFor', s)
      const hero = v.hero || v.line
      return hero ? hero.number : ''
    }
  },
  Unit: {
    hide: (el, s) => {
      const v = (s && s.recordsViewModel) || el.call('assistantRecordsViewFor', s)
      const hero = v.hero || v.line
      return !(hero && hero.unit)
    },
    text: (el, s) => {
      const v = (s && s.recordsViewModel) || el.call('assistantRecordsViewFor', s)
      const hero = v.hero || v.line
      return (hero && hero.unit) || ''
    }
  }
}

// One list row: a real button that opens the record; the title wraps, the
// facts follow in `caption`. When the app declares its OWN row for the
// collection (manifest `collections[].row`), that row draws the record inside
// the same button (RowApp) and the two generic lines step aside; the button
// keeps the click, so an app row is read-only.
export const AssistantRecordsRow = {
  tag: 'li',
  AssistantRecordsRowBtn: {
    tag: 'button',
    type: 'button',
    width: '100%',
    flow: 'y',
    gap: 'X',
    align: 'flex-start',
    padding: 'Y Z',
    round: 'radiusControl',
    border: 'none',
    background: 'transparent',
    color: 'title',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'background A defaultBezier',
    ':not(:disabled):hover': { background: 'hover' },
    ':not(:disabled):active': { background: 'selected' },
    // A row opens its record only where a page names it (functions/
    // assistantRecords.js assistantRecordsHref: the record route, else the
    // app's page). One that opens nothing is no control: no pointer, no
    // hover, out of the tab order.
    ':disabled': { cursor: 'default' },
    disabled: (el, s) => (el.call('assistantRecordsHref', s, s && s.row) ? null : true),
    // A FINGER'S TARGET (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1): on a touch screen a row
    // that opens something is 44pt tall — the generated 'open incidents by
    // severity' widget's bars read 39px at 393 and 34px at 834 on dev DFI (the
    // Finances lane's shell check). Real height, not a slop: stacked rows' slops
    // would overlap their neighbours.
    '@coarse': { minHeight: 'controlH', justifyContent: 'center' },
    ariaLabel: (el, s) =>
      String(el.call('polyglot', 'workspace.assistantRecords.openRow')).replace(
        '{title}',
        (s && s.title) || ''
      ),
    onClick: (e, el, s) => el.call('assistantRecordsOpen', s, s && s.row),
    RowTitle: {
      tag: 'span',
      hide: (el, s) => !!(s && s.appRow),
      fontSize: 'A',
      fontWeight: '500',
      overflowWrap: 'anywhere',
      text: (el, s) => (s && s.title) || ''
    },
    RowMeta: {
      tag: 'span',
      hide: (el, s) => !(s && s.meta) || !!(s && s.appRow),
      fontSize: 'Z',
      color: 'caption',
      overflowWrap: 'anywhere',
      text: (el, s) => (s && s.meta) || ''
    },
    // THE STATUS (D5, Pulse's incidents): a dot in the row's semantic ink
    // (successInk / warningInk / dangerInk / accentInk; caption when the
    // status names no tone) and the status words in caption.
    RowStatus: {
      tag: 'span',
      dataRecordsRowStatus: true,
      if: (el, s) => !!(s && s.status) && !(s && s.appRow),
      flow: 'x',
      align: 'center',
      gap: 'X',
      fontSize: 'Z',
      color: 'caption',
      StatusDot: {
        tag: 'span',
        dataStatusDot: true,
        dataTone: (el, s) => (s && s.tone) || 'caption',
        boxSize: 'Y',
        flexShrink: '0',
        round: 'radiusPill',
        background: (el, s) => (s && s.tone) || 'caption'
      },
      StatusText: {
        tag: 'span',
        overflowWrap: 'anywhere',
        text: (el, s) => (s && s.status) || ''
      }
    },
    // The app's own row, mounted under the app's context with scope
    // { record, fields } (functions/assistantRecords.js
    // assistantRecordsRowMount). Absent without an app row, so the generic
    // row keeps exactly its two lines.
    RowApp: {
      if: (el, s) => !!(s && s.appRow),
      alignSelf: 'stretch',
      minWidth: '0',
      onRender: (el, s) => el.call('assistantRecordsRowMount', el, s),
      onUpdate: (el, s) => el.call('assistantRecordsRowMount', el, s)
    }
  }
}

export const AssistantRecordsHeadCell = {
  tag: 'th',
  attr: { scope: 'col' },
  textAlign: 'left',
  padding: 'Y Z',
  fontWeight: '500',
  color: 'caption',
  overflowWrap: 'anywhere',
  text: (el, s) => (s && s.label) || ''
}

// One table row: the row is the control (Enter / Space open it) — where a page
// names its record (functions/assistantRecords.js assistantRecordsHref). A row
// that opens nothing only reads: no pointer, no hover, out of the tab order.
export const AssistantRecordsTableRow = {
  tag: 'tr',
  dataRecordsOpen: (el, s) => (el.call('assistantRecordsHref', s, s && s.row) ? true : null),
  tabindex: (el, s) => (el.call('assistantRecordsHref', s, s && s.row) ? '0' : null),
  cursor: 'default',
  // A FINGER'S TARGET (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1): on a touch screen a row
  // that opens something is 44pt tall — the generated 'open incidents by
  // severity' widget's bars read 39px at 393 and 34px at 834 on dev DFI (the
  // Finances lane's shell check). Real height, not a slop: stacked rows' slops
  // would overlap their neighbours.
  // A table row's `height` is its floor.
  '@coarse': { height: 'controlH' },
  transition: 'background A defaultBezier',
  '&[data-records-open]': { cursor: 'pointer' },
  '&[data-records-open]:hover': { background: 'hover' },
  '&[data-records-open]:active': { background: 'selected' },
  onClick: (e, el, s) => el.call('assistantRecordsOpen', s, s && s.row),
  onKeydown: (e, el, s) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    el.call('assistantRecordsOpen', s, s && s.row)
  },
  childrenAs: 'state',
  children: (el, s) => (s && Array.isArray(s.cells) ? s.cells : []),
  childExtends: 'AssistantRecordsCell'
}

export const AssistantRecordsCell = {
  tag: 'td',
  padding: 'Y Z',
  color: 'title',
  overflowWrap: 'anywhere',
  text: (el, s) => (s && s.text) || ''
}

// THE DRILL CONTRACT of a category row (D2 drill-down), in ONE place for every
// row that opens its records: a bar (AssistantRecordsBar) and a chip
// (AssistantRecordsChip). On a board card (`from`) a drillable row is a
// CONTROL: a button named after its value, Enter / Space / a click open that
// value's records as a list card after this one, a hover and a press fill.
// Anywhere else, and for the fold ("Other") or the empty group, it only reads.
// The touch floor stays each row's own `@coarse` (a bar and a chip differ).
export const AssistantRecordsDrillTarget = {
  role: (el, s) => (s && s.drillable && s.from ? 'button' : null),
  tabindex: (el, s) => (s && s.drillable && s.from ? '0' : null),
  ariaLabel: (el, s) =>
    s && s.drillable && s.from
      ? String(el.call('polyglot', 'workspace.assistantRecords.drillOpen')).replace(
          '{value}',
          s.label || ''
        )
      : null,
  transition: 'background A defaultBezier',
  '&[role="button"]': { cursor: 'pointer' },
  '&[role="button"]:hover': { background: 'hover' },
  '&[role="button"]:active': { background: 'selected' },
  onClick: (e, el, s) => el.call('assistantRecordsDrill', s),
  onKeydown: (e, el, s) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    if (!(s && s.drillable && s.from)) return
    e.preventDefault()
    el.call('assistantRecordsDrill', s)
  }
}

// One bar: the label and the number on one line, the bar under them. A row
// keyed by DATA declares its tag — a group named 'label' or 'time' would
// otherwise render as that element. On a board card a category bar is a
// CONTROL (D2 drill-down, AssistantRecordsDrillTarget): Enter / Space / a
// click open that value's records as a list card after this one. The inset
// and the radius are every bar's, so a control and the plain "Other" line up.
export const AssistantRecordsBar = {
  extends: 'AssistantRecordsDrillTarget',
  tag: 'div',
  flow: 'y',
  gap: 'X',
  minWidth: '0',
  padding: 'X Y',
  round: 'radiusControl',
  // A FINGER'S TARGET (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1): on a touch screen a row
  // that opens something is 44pt tall — the generated 'open incidents by
  // severity' widget's bars read 39px at 393 and 34px at 834 on dev DFI (the
  // Finances lane's shell check). Real height, not a slop: stacked rows' slops
  // would overlap their neighbours.
  // A bar that only reads (not drillable) keeps the desktop density.
  '@coarse': { '&[role="button"]': { minHeight: 'controlH', justifyContent: 'center' } },
  BarHead: {
    flow: 'x',
    flexWrap: 'wrap',
    align: 'center',
    gap: 'Z',
    fontSize: 'Z',
    // A donut's legend row (D5): the slice's dot — the card hue at the
    // slice's step — for the track.
    BarDot: {
      tag: 'span',
      if: (el, s) => !!(s && s.dot != null),
      boxSize: 'Y',
      flexShrink: '0',
      round: 'radiusPill',
      background: 'currentColor',
      opacity: (el, s) => (s && s.dot != null ? String(s.dot) : undefined)
    },
    BarLabel: {
      tag: 'span',
      flex: '1',
      minWidth: '0',
      color: 'title',
      overflowWrap: 'anywhere',
      text: (el, s) => (s && s.label) || ''
    },
    BarValue: {
      tag: 'span',
      color: 'title',
      fontWeight: '500',
      maxWidth: '100%',
      overflowWrap: 'anywhere',
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
      text: (el, s) =>
        s && s.shareText ? (s.value || '') + ' · ' + s.shareText : (s && s.value) || ''
    }
  },
  BarTrack: {
    if: (el, s) => !(s && s.dot != null),
    height: 'Y',
    round: 'radiusPill',
    background: 'overlay',
    overflow: 'hidden',
    BarFill: {
      height: '100%',
      round: 'radiusPill',
      background: 'currentColor',
      opacity: '0.55',
      style: (el, s) => ({ width: Math.round(((s && s.share) || 0) * 100) + '%' })
    }
  }
}

// One chip (the display hint 'chips', board binding A′): the board's chip-row
// pill (AiWidgetChip, its label as the text) with the group's count after the
// label. A long label wraps inside the pill; the count never breaks. On a
// board card a category chip is a CONTROL like a bar: the same drill contract
// (AssistantRecordsDrillTarget), so the same name, keys and states. A row
// keyed by DATA declares its tag.
export const AssistantRecordsChip = {
  extends: ['AiWidgetChip', 'AssistantRecordsDrillTarget'],
  tag: 'span',
  flow: 'x',
  align: 'center',
  gap: 'X',
  minWidth: '0',
  maxWidth: '100%',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  // A finger's target, as a bar's: a chip that opens something is controlH
  // tall on a touch screen; a chip that only reads keeps the pill's density.
  '@coarse': { '&[role="button"]': { minHeight: 'controlH' } },
  ChipCount: {
    tag: 'span',
    dataRecordsChipCount: true,
    flexShrink: '0',
    color: 'caption',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
    text: (el, s) => (s && s.value) || ''
  }
}

// One slice of the donut (D5): a dashed circle on the ring, its share as
// the dash and its start as the offset (a 100-unit circumference), the card
// hue at the slice's opacity step. A row keyed by data declares its tag.
export const AssistantRecordsSlice = {
  tag: 'circle',
  attr: {
    cx: '21',
    cy: '21',
    r: '15.9155',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '6',
    'stroke-dasharray': (el, s) => (s && s.dash) || '0 100',
    'stroke-dashoffset': (el, s) => (s && s.offset) || '25',
    'stroke-opacity': (el, s) => (s && s.opacity != null ? String(s.opacity) : '1')
  }
}

// A TIME SERIES of a records answer (render step 2): the newest value as the
// home widget's HERO (the numeral, its money code leading, the date as the
// grey word after it), the line and its 8% area (the home sparkline's
// geometry, functions/homeWidgetVisuals.js homeSparkGeom), ONE accent dot on
// the newest point, and the first and the last date under the plot. The svg
// is drawn data (not an icon); its words ride the element's accessible name.
// The dot is an element, not an svg circle: the plot stretches to its box and
// a circle would stretch with it.
export const AssistantRecordsLine = {
  flow: 'y',
  gap: 'Y',
  minWidth: '0',
  role: 'img',
  ariaLabel: (el, s) => {
    const line = ((s && s.recordsViewModel) || el.call('assistantRecordsViewFor', s)).line
    return line ? line.first + ' – ' + line.last + ' · ' + line.value : null
  },
  AssistantRecordsHero: {
    Tail: {
      text: (el, s) => {
        const line = ((s && s.recordsViewModel) || el.call('assistantRecordsViewFor', s)).line
        return (line && line.last) || ''
      }
    }
  },
  LinePlot: {
    position: 'relative',
    height: 'D',
    minWidth: '0',
    Chart: {
      // frank-allow FA601 — data plot (non-icon viewBox), not an icon
      tag: 'svg',
      viewBox: '0 0 240 64',
      preserveAspectRatio: 'none',
      ariaHidden: 'true',
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      display: 'block',
      overflow: 'visible',
      Area: {
        // frank-allow FA602 — computed area, not an icon
        tag: 'path',
        attr: {
          d: (el, s) => {
            const line = ((s && s.recordsViewModel) || el.call('assistantRecordsViewFor', s)).line
            const g =
              s && s.recordsViewModel
                ? s.recordsGeometry
                : line
                  ? el.call('homeSparkGeom', line.points, 240, 64, 4)
                  : null
            return g ? g.areaPath : null
          },
          fill: 'currentColor',
          'fill-opacity': '0.08',
          stroke: 'none'
        }
      },
      Line: {
        // frank-allow FA602 — computed stroke, not an icon
        tag: 'path',
        attr: {
          d: (el, s) => {
            const line = ((s && s.recordsViewModel) || el.call('assistantRecordsViewFor', s)).line
            const g =
              s && s.recordsViewModel
                ? s.recordsGeometry
                : line
                  ? el.call('homeSparkGeom', line.points, 240, 64, 4)
                  : null
            return g ? g.linePath : null
          },
          fill: 'none',
          stroke: 'currentColor',
          'stroke-width': '2',
          'stroke-linejoin': 'round',
          'stroke-linecap': 'round',
          'vector-effect': 'non-scaling-stroke'
        }
      }
    },
    LineDot: {
      dataLineDot: true,
      position: 'absolute',
      boxSize: 'Y',
      round: 'radiusPill',
      background: 'currentColor',
      transform: 'translate(-50%, -50%)',
      hide: (el, s) => {
        const line = ((s && s.recordsViewModel) || el.call('assistantRecordsViewFor', s)).line
        return !(s && s.recordsViewModel
          ? s.recordsGeometry
          : line && el.call('homeSparkGeom', line.points, 240, 64, 4))
      },
      left: (el, s) => {
        const line = ((s && s.recordsViewModel) || el.call('assistantRecordsViewFor', s)).line
        const g =
          s && s.recordsViewModel
            ? s.recordsGeometry
            : line
              ? el.call('homeSparkGeom', line.points, 240, 64, 4)
              : null
        return g ? Math.round((g.end.x / 240) * 1000) / 10 + '%' : null
      },
      top: (el, s) => {
        const line = ((s && s.recordsViewModel) || el.call('assistantRecordsViewFor', s)).line
        const g =
          s && s.recordsViewModel
            ? s.recordsGeometry
            : line
              ? el.call('homeSparkGeom', line.points, 240, 64, 4)
              : null
        return g ? Math.round((g.end.y / 64) * 1000) / 10 + '%' : null
      }
    }
  },
  LineAxis: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    fontSize: 'Y',
    color: 'caption',
    minWidth: '0',
    LineFirst: {
      tag: 'span',
      text: (el, s) => {
        const line = ((s && s.recordsViewModel) || el.call('assistantRecordsViewFor', s)).line
        return (line && line.first) || ''
      }
    },
    LineLast: {
      tag: 'span',
      text: (el, s) => {
        const line = ((s && s.recordsViewModel) || el.call('assistantRecordsViewFor', s)).line
        return (line && line.last) || ''
      }
    }
  }
}
